import React, { useEffect, useState } from 'react'
import { connect, useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'

import { Button, Grid, IconButton, TextField, Tooltip, withStyles } from '@material-ui/core'
import { Close, InfoOutlined } from '@material-ui/icons'
import { DatePicker } from '@material-ui/pickers'
import CurrencyTextField from '@unicef/material-ui-currency-textfield/dist/CurrencyTextField'

import { acGetCurrentPlanTable, acGetPlanTables, acGetValidPlans, acInsertPlanTableData } from '../../class/actions'
import { Label, Loader, SelectField } from '../default'

const FormPlanTable = ({
  classes,
  currentTable, currentTableError, loadingCurrentTable,
  plans, plansError, loadingPlans,
  planTablesError, loadingPlanTables, planTables,
}) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const PLAN_TABLE_PREFIX = 'Tabela de Planos'

  const [name, setName] = useState(PLAN_TABLE_PREFIX)
  const [nameError, setNameError] = useState(null)
  const [description, setDescription] = useState('')
  const [validUntil, setValidUntil] = useState(null)
  const [versions, setVersions] = useState([])
  const [selectedVersion, setSelectedVersion] = useState(null)
  const [planTable, setPlanTable] = useState([null])

  useEffect(() => {
    if (!currentTable)
      dispatch(acGetCurrentPlanTable())

    if (!plans)
      dispatch(acGetValidPlans())

    if (!planTables || !planTables.length)
      dispatch(acGetPlanTables())
  }, [dispatch, plans, currentTable, planTables])

  useEffect(() => {
    if (planTables && planTables.length) {
      // Get all unique version values
      setVersions([...new Set(planTables.map(item => item.version))])
    }
  }, [planTables])

  useEffect(() => {
    if (!loadingCurrentTable && currentTable && planTable.length <= 1) {
      setPlanTable(currentTable.plans.map(plan => ({
        plan_id: plan.plan_id,
        price: plan.PlanPrices.price
      })))
    }
  }, [currentTable, loadingCurrentTable, planTable])

  useEffect(() => {
    dispatch(acInsertPlanTableData({
      name: name,
      description: description,
      valid_until: validUntil ? validUntil.toISOString().split('T')[0] : null,
      plans: planTable,
      version: selectedVersion
    }))
  }, [dispatch, validUntil, name, description, planTable, selectedVersion])

  const setPlanEntryPrice = (price, index) => {
    setPlanTable(planTable.map((item, idx) => index === idx ? item === null ? {
      plan_id: null,
      price: price
    } : { ...item, price: price } : item))
  }

  const setPlanEntryValue = (value, index) => {
    setPlanTable(planTable.map((item, idx) => index === idx ? item === null ? {
      plan_id: value,
      price: 0
    } : { ...item, plan_id: value } : item))
  }

  const removePlanTableEntry = index => {
    setPlanTable(planTable.filter((_, idx) => idx !== index))
  }

  const expandEntryList = () => {
    if (!planTable.some(item => item === null))
      setPlanTable(planTable.concat(null))
  }

  const validateName = value => {
    setName(value)
    if (!value.includes(PLAN_TABLE_PREFIX)) {
      setNameError(`O nome da tabela deve conter "${PLAN_TABLE_PREFIX}"`)
      return
    }

    if (!value.length) {
      setNameError('O nome da tabela não pode ser vazio!')
      return
    }

    setNameError(null)
  }

  const renderPlanEntry = (plan, index) => {
    return <Grid key={index} container spacing={2}>
      {
        (!loadingPlans && plans) &&
        <>
          <Grid item xs={7}>
            <SelectField id={index.toString()}
              label={t('modals.plans.insert.plan')}
              items={plans.map(p => ({
                name: p.name,
                value: p.plan_id
              }))}
              value={plan === null ? '' : planTable[index].plan_id}
              onChange={e => setPlanEntryValue(e.target.value, index)} />
          </Grid>
          <Grid item xs={3}>
            <CurrencyTextField
              variant='filled'
              label={t('modals.plans.monthlyPrice')}
              value={plan === null ? 0 : planTable[index].price}
              currencySymbol="R$"
              outputFormat="number"
              decimalCharacter=","
              digitGroupSeparator="."
              onChange={(e, v) => setPlanEntryPrice(parseFloat(v), index)}
              style={{ width: '100%' }} />
          </Grid>
          {
            index !== 0 &&
            <Grid item xs={2}>
              <Tooltip title={t('default.removeItemFromList')}>
                <IconButton onClick={() => removePlanTableEntry(index)}>
                  <Close />
                </IconButton>
              </Tooltip>
            </Grid>
          }
        </>
      }
    </Grid>
  }

  return (
    <Grid container spacing={2} className={classes.grid}>

      <Grid item xs={12}>
        <TextField
          required
          variant='filled'
          type="text"
          label={t('forms.planTable.tableName')}
          value={name}
          onChange={(e) => validateName(e.target.value)}
          error={nameError ? true : false}
          helperText={nameError ? nameError : null}
          style={{ width: '100%' }} />
      </Grid>

      <Grid item xs={12}>
        <TextField
          variant='filled'
          type="text"
          label={t('forms.planTable.tableDescription')}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          style={{ width: '100%' }} />
      </Grid>

      <Grid item xs={6}>
        <div className={classes.tooltipContainer}>
          <DatePicker
            label={t('forms.planTable.validUntil')}
            format="dd/MM/yyyy"
            inputVariant="outlined"
            value={validUntil}
            onChange={e => setValidUntil(e)}
            style={{ width: '100%' }}
            required
          />
        </div>
        <Tooltip title={t('forms.planTable.validUntilTooltip')} style={{ marginLeft: '10px' }}>
          <InfoOutlined fontSize='small' />
        </Tooltip>
      </Grid>

      <Grid item xs={6}>
        <div className={classes.tooltipContainer}>
          <SelectField
            label={t('forms.planTable.version')}
            items={versions.map(v => ({
              name: (currentTable && currentTable.version) === v ? `${v} (${t('forms.customerRegister.default')})` : v,
              value: v
            }))}
            value={selectedVersion}
            onChange={e => setSelectedVersion(e.target.value)} />
        </div>
        <Tooltip title={(
          <div>
            Diferenças entre as versões:<br />
            Versão 1:<br />
            - Carga mínima: R$ 2,99 para todos os usuários<br />
            - Compra de planos: qualquer tipo de pagamento<br />
            Versão 2:<br />
            - Carga mínima: R$ 15,00 para usuários sem planos, R$ 2,99 para usuários com planos<br />
            - Compra de planos: apenas cartão de crédito
          </div>
        )} style={{ marginLeft: '10px' }}>
          <InfoOutlined fontSize='small' />
        </Tooltip>
      </Grid>

      <Grid item xs={12}>
        <Label bold>
          {t('forms.planTable.plans')}
        </Label>
      </Grid>
      <Grid item xs={12}>
        {
          (!loadingPlans && !loadingCurrentTable) ?
            planTable.map((p, index) => renderPlanEntry(p, index))
            : <Loader />
        }
      </Grid>
      <Grid item xs={6}>
        <Button onClick={expandEntryList} style={{ width: '100%', backgroundColor: '#ddd', color: 'black' }}>
          {t('modals.planTable.expandEntryList')}
        </Button>
      </Grid>
    </Grid>
  )
}

const styles = theme => ({
  grid: {
    display: 'flex',
    flex: 1
  },
  tooltipContainer: {
    display: 'inline-block',
    width: 'calc(100% - 35px)',
  }
})

const mapStateToProps = ({ ReducerPlanTable, ReducerPlan }) => ({
  currentTable: ReducerPlanTable.currentTable,
  currentTableError: ReducerPlanTable.currentTableError,
  loadingCurrentTable: ReducerPlanTable.loadingCurrentTable,

  planTables: ReducerPlanTable.planTables,
  planTablesError: ReducerPlanTable.planTablesError,
  loadingPlanTables: ReducerPlanTable.loadingPlanTables,

  plans: ReducerPlan.plans,
  plansError: ReducerPlan.plansError,
  loadingPlans: ReducerPlan.loadingPlans
})

export default connect(mapStateToProps)(withStyles(styles)(FormPlanTable))